/**
 * Route Permission Guard
 * Checks user permissions before allowing navigation to protected routes
 */

import {
  mapRouteToTab,
  mapRouteToSettingsTab,
  canViewTab,
  canViewSettingsTab,
  isOwnerUser,
} from "@/utils/permissions";
import { getUserData } from "@/auth/utils";

/**
 * List of public routes that don't require permission checks
 */
const PUBLIC_ROUTES = [
  "auth-login",
  "login",
  "register",
  "forgot-password",
  "reset-password",
  "error-404",
  "unauthorized",
  "error",
  "misc-not-authorized",
  "account-verification",
  "login-as",
];

/**
 * Routes categorized by required tab permission
 */
const PROTECTED_ROUTES = {
  documents: "activeProjects",
  proposals: "proposals",
  archive: "archivedProjects",
  "archived-project-folder": "archivedProjects",
  "archived-document": "archivedProjects",
  payments: "payments",
  invoices: "invoices",
  settings: "settings",
};

/**
 * Settings routes that require specific settings tab permission
 */
const SETTINGS_ROUTES = {
  "account-setting-account": "accounts",
  "account-setting-password": "security",
  "account-setting-security": "security",
  "account-setting-notification": "notifications",
  subscriptions: "billingPlans",
  seats: "seats",
};

/**
 * Check if route requires permission checks
 */
export function isProtectedRoute(routeName) {
  return (
    PROTECTED_ROUTES.hasOwnProperty(routeName)
    || SETTINGS_ROUTES.hasOwnProperty(routeName)
  );
}

/**
 * Check if user has permission to access a route
 */
export function hasRoutePermission(store, routeName) {
  // Public routes don't require permission checks
  if (PUBLIC_ROUTES.includes(routeName)) {
    return true;
  }

  const userData = getUserData();

  if (routeName === "proposals" && userData?.role === "client") {
    return true;
  }

  // Owner/Admin users have access to everything
  if (isOwnerUser(store)) {
    return true;
  }

  // Dashboard is always accessible (permissions are checked in the Dashboard component)
  if (routeName === "dashboard") {
    return true;
  }

  // Check tab permissions
  if (PROTECTED_ROUTES.hasOwnProperty(routeName)) {
    const requiredTab = PROTECTED_ROUTES[routeName];

    // Special check for settings route - user needs access to at least one settings tab
    if (requiredTab === "settings") {
      const permissions = store.state.permissions?.permissions || {};
      const settingsPerms = permissions.settings || {};

      // Check if user has any settings tab access
      const hasAnySettingsAccess = Object.values(settingsPerms).some(perm => {
        if (typeof perm === "object" && perm !== null) {
          return perm.view === true; // For seats, billingPlans
        }
        return perm === true; // For accounts, security, notifications, etc.
      });

      return hasAnySettingsAccess;
    }

    return canViewTab(store, requiredTab);
  }

  // Check settings sub-tab permissions (for old-style routes like account-setting-account)
  if (SETTINGS_ROUTES.hasOwnProperty(routeName)) {
    const requiredSettingsTab = SETTINGS_ROUTES[routeName];
    return canViewSettingsTab(store, requiredSettingsTab);
  }

  // Default to allow (for non-protected routes)
  return true;
}

/**
 * Create route guard function for router
 */
export function createPermissionGuard(store) {
  return (to, from, next) => {
    const routeName = to.name;

    // Skip permission checks during initial load
    if (!store.getters["permissions/isPermissionsLoaded"]) {
      console.log("⏳ Permissions not loaded yet, skipping permission guard");
      next();
      return;
    }

    // Skip public routes
    if (PUBLIC_ROUTES.includes(routeName)) {
      next();
      return;
    }

    // Check permissions for protected routes
    if (!hasRoutePermission(store, routeName)) {
      console.warn(
        `[Permission Guard] User denied access to route: ${routeName}`,
      );
      next(false); // Cancel navigation
      return;
    }

    next();
  };
}
